// "Fly to" for the orbit camera: the maths (pure) and a small controller
// that drives the OrbitControls target + camera for one short glide, then
// lets go. CameraFlight.tsx mounts the controller inside the <Canvas>.
//
// A flight translates the camera: the offset from the target to the camera
// (zoom distance + viewing angle) stays exactly as the player left it; only
// the point being looked at moves. It is one-shot and interruptible:
//   - it runs once per request (keyed on `seq`, so a repeated minimap click
//     on the same spot still flies) and then stops touching the camera;
//   - any input on the controls (their `start` event: pointer, wheel, touch)
//     cancels it on the spot;
//   - while a milestone cinematic owns the camera (CinematicCameraRig sets
//     `controls.enabled = false`) requests are dropped and a running flight
//     is cancelled, so the cinematic's pose save/restore stays exact.

export type Vec3 = readonly [number, number, number];

/** A request to glide the camera to look at `position`. */
export interface CameraFocusRequest {
  readonly position: Vec3;
  /**
   * The same point in board-space (unscaled) coordinates, for consumers that
   * compare against snapshot transforms (audio biome lookup). `position` is
   * what the camera flies to — already in rendered world space.
   */
  readonly boardPosition?: Vec3;
  /** Bump for every request: a new seq flies again even to the same point. */
  readonly seq: number;
  /**
   * `always` (minimap, pause-card "Go to"): fly regardless.
   * `if-off-screen` (selecting a tile / city / unit): fly only when the point
   * is off screen or near its edge, so clicking things you can already see
   * doesn't yank the view around.
   */
  readonly when?: 'always' | 'if-off-screen';
}

export interface FlightPose {
  readonly position: Vec3;
  readonly target: Vec3;
}

export interface FlightPlan {
  readonly fromTarget: Vec3;
  readonly toTarget: Vec3;
  /** camera position − target, kept constant for the whole flight. */
  readonly offset: Vec3;
  readonly durationMs: number;
}

export const MIN_FLIGHT_MS = 450;
export const MAX_FLIGHT_MS = 700;
/** Extra ms per world unit travelled; ~40 units (a map's width) hits the cap. */
const MS_PER_UNIT = 6.25;
/** Closer than this the camera is already there. */
export const ARRIVED_EPSILON = 1e-3;
/** NDC radius (0 = centre, 1 = screen edge) beyond which a point counts as "near the edge". */
export const SCREEN_EDGE_NDC = 0.7;

const sub = (a: Vec3, b: Vec3): Vec3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const add = (a: Vec3, b: Vec3): Vec3 => [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
const lerp = (a: Vec3, b: Vec3, t: number): Vec3 => [
  a[0] + (b[0] - a[0]) * t,
  a[1] + (b[1] - a[1]) * t,
  a[2] + (b[2] - a[2]) * t,
];
export const distance = (a: Vec3, b: Vec3): number => Math.hypot(...sub(a, b));

/** 450 ms for a hop, growing with distance up to 700 ms; 0 with reduced motion. */
export function flightDurationMs(dist: number, reducedMotion = false): number {
  if (reducedMotion) return 0;
  return Math.min(MAX_FLIGHT_MS, MIN_FLIGHT_MS + Math.max(0, dist) * MS_PER_UNIT);
}

/** Ease-in-out cubic, clamped to [0, 1]. */
export function easeInOutCubic(t: number): number {
  const x = Math.min(1, Math.max(0, t));
  return x < 0.5 ? 4 * x * x * x : 1 - (-2 * x + 2) ** 3 / 2;
}

/** The flight from `pose` to look at `to`, or null when the camera is already there. */
export function planFlight(pose: FlightPose, to: Vec3, reducedMotion = false): FlightPlan | null {
  const dist = distance(pose.target, to);
  if (dist < ARRIVED_EPSILON) return null;
  return {
    fromTarget: pose.target,
    toTarget: to,
    offset: sub(pose.position, pose.target),
    durationMs: flightDurationMs(dist, reducedMotion),
  };
}

/** Pose `elapsedMs` into the flight, and whether it has landed. */
export function sampleFlight(
  plan: FlightPlan,
  elapsedMs: number,
): { pose: FlightPose; done: boolean } {
  const done = plan.durationMs <= 0 || elapsedMs >= plan.durationMs;
  const k = done ? 1 : easeInOutCubic(elapsedMs / plan.durationMs);
  const target = done ? plan.toTarget : lerp(plan.fromTarget, plan.toTarget, k);
  return { pose: { target, position: add(target, plan.offset) }, done };
}

/**
 * Whether a point projected to normalised device coordinates is off screen
 * or near its edge (|x| or |y| beyond `edge`), or behind the camera (z > 1).
 */
export function isNearScreenEdge(
  ndc: { x: number; y: number; z: number },
  edge = SCREEN_EDGE_NDC,
): boolean {
  if (!Number.isFinite(ndc.x) || !Number.isFinite(ndc.y) || ndc.z > 1 || ndc.z < -1) return true;
  return Math.abs(ndc.x) > edge || Math.abs(ndc.y) > edge;
}

// ── controller ──────────────────────────────────────────────────────────

interface Settable {
  x: number;
  y: number;
  z: number;
  set(x: number, y: number, z: number): unknown;
}

/** The slice of three-stdlib's OrbitControls a flight needs. */
export interface FlightControls {
  readonly target: Settable;
  /** false while a cinematic owns the camera (CinematicCameraRig.setActive). */
  enabled: boolean;
  addEventListener(type: 'start', listener: () => void): void;
  removeEventListener(type: 'start', listener: () => void): void;
}

export interface FlightCamera {
  readonly position: Settable;
}

export interface FlightDeps {
  now(): number;
  prefersReducedMotion(): boolean;
  /** For `if-off-screen` requests: is this point off screen or near the edge? */
  isNearEdge(point: Vec3): boolean;
}

export type RequestOutcome =
  | 'flying'
  | 'jumped' // reduced motion: moved in one step
  | 'arrived' // already looking there
  | 'on-screen' // `if-off-screen` and the point is comfortably visible
  | 'cinematic' // a cinematic owns the camera: dropped
  | 'repeat' // this seq was already handled
  | 'detached'; // no controls yet

export class CameraFlightController {
  private controls: FlightControls | null = null;
  private camera: FlightCamera | null = null;
  private plan: FlightPlan | null = null;
  private startedAt = 0;
  private lastSeq: number | null = null;
  private readonly onStart = () => this.cancel();

  constructor(private readonly deps: FlightDeps) {}

  /** Bind to the live controls + camera; returns the matching detach. */
  attach(controls: FlightControls, camera: FlightCamera): () => void {
    this.detach();
    this.controls = controls;
    this.camera = camera;
    controls.addEventListener('start', this.onStart);
    return () => {
      if (this.controls === controls) this.detach();
    };
  }

  get flying(): boolean {
    return this.plan !== null;
  }

  request(req: CameraFocusRequest): RequestOutcome {
    if (this.lastSeq === req.seq) return 'repeat';
    const controls = this.controls;
    const camera = this.camera;
    if (!controls || !camera) return 'detached';
    this.lastSeq = req.seq;
    if (!controls.enabled) {
      // A cinematic has the camera. Dropping (not queueing) keeps its
      // hand-back pose exact; the player can simply click again.
      return 'cinematic';
    }
    if (req.when === 'if-off-screen' && !this.deps.isNearEdge(req.position)) return 'on-screen';
    const t = controls.target;
    const p = camera.position;
    const plan = planFlight(
      { target: [t.x, t.y, t.z], position: [p.x, p.y, p.z] },
      req.position,
      this.deps.prefersReducedMotion(),
    );
    if (!plan) {
      this.plan = null;
      return 'arrived';
    }
    this.plan = plan;
    this.startedAt = this.deps.now();
    if (plan.durationMs <= 0) {
      this.frame();
      return 'jumped';
    }
    return 'flying';
  }

  /** Once per rendered frame, after OrbitControls.update(). */
  frame(): void {
    const plan = this.plan;
    const controls = this.controls;
    const camera = this.camera;
    if (!plan || !controls || !camera) return;
    if (!controls.enabled) {
      this.cancel();
      return;
    }
    const { pose, done } = sampleFlight(plan, this.deps.now() - this.startedAt);
    controls.target.set(pose.target[0], pose.target[1], pose.target[2]);
    camera.position.set(pose.position[0], pose.position[1], pose.position[2]);
    if (done) this.plan = null;
  }

  /** Stop where the camera is now; nothing snaps back. */
  cancel(): void {
    this.plan = null;
  }

  private detach(): void {
    this.controls?.removeEventListener('start', this.onStart);
    this.controls = null;
    this.camera = null;
    this.plan = null;
  }
}
